import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { FeatureCommitHistoryState } from './features/feature-commits-history.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {
  /* report time interval options */
  head: URLOptions.head,
  from: URLState.option<string|undefined>({
    name: 'from',
    default: undefined,
    encode: head => head,
    decode: head => head,
  }),
  until: URLOptions.until,
  viewSince: URLOptions.viewSince,
  viewUntil: URLOptions.viewUntil,
  timelineSplit: URLOptions.timelineSplit,
};

@customElement('page-project-history')
export class PageProjectHistory extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, head?: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'history'].join('/'), URLState.toSearchParams(pageSchema, {
      head: options.head,
    }), options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/history',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-history
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-history>
      `),
    }];
  }

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  private _urlState = new URLState(this, pageSchema);

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'History',
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;

    const linkRenderer = this._urlState.createLinkRenderer();
    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"history"}
      ></project-header>
      <project-body wide user-role=${this._project.access}>

        <feature-commit-history
          .orgSlug=${this.orgSlug}
          .projectSlug=${this.projectSlug}
          .state=${{
            timelineSplit: this._urlState.state.timelineSplit,
            head: this._urlState.state.head,
            until: this._urlState.state.until,
            viewSince: this._urlState.state.viewSince,
            viewUntil: this._urlState.state.viewUntil,
            selectedCommitId: this._urlState.state.from,
          } satisfies FeatureCommitHistoryState}
          .linkifier=${(options: FeatureCommitHistoryState) => linkRenderer({
            head: options.head,
            until: options.until,
            viewSince: options.viewSince,
            viewUntil: options.viewUntil,
            timelineSplit: options.timelineSplit,
            from: options.selectedCommitId,
          })}
        ></feature-commit-history>
      </project-body>
      <app-footer></app-footer>
    `
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles];
}
